var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { forwardRef, isValidElement, PureComponent } from 'react';
import { LinkTarget, Size } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { DataContext, RouteContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { bool, element, eventListener, expression, func, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent, HoverEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { HistoryService, MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isContentfulString, isEnumValid, isFunction, isValidURL } from '@philips-internal/oswald-ui-utils';
import { InlineImage } from '../inline-image/inline-image.js';
import { LinkAPI } from './link-api.js';
import { LinkFontFamily } from './link-constants.js';
import './link.css';
let Link = class Link extends PureComponent {
    constructor() {
        super(...arguments);
        this.onClick = (event, routeContext) => {
            const { onClick, preventDefault, route, stopPropagation } = this.props;
            if (stopPropagation) {
                event.stopPropagation();
            }
            if (preventDefault) {
                event.preventDefault();
            }
            if (route) {
                if (isValidURL(route)) {
                    window.open(route);
                    return;
                }
                const completeRoute = routeContext.nestedRoute(route);
                HistoryService.push(completeRoute, this.context);
            }
            else {
                isFunction(onClick) && onClick(new ClickEvent({
                    preventDefault: () => event.preventDefault(),
                    stopPropagation: () => event.stopPropagation(),
                }));
            }
        };
        this.onDoubleClick = () => {
            const { onDoubleClick } = this.props;
            isFunction(onDoubleClick) && onDoubleClick(new ClickEvent());
        };
        this.onContextMenuClick = () => {
            const { onContextMenuClick } = this.props;
            isFunction(onContextMenuClick) && onContextMenuClick(new ClickEvent());
        };
        this.onMouseMove = () => {
            const { onMouseMove } = this.props;
            isFunction(onMouseMove) && onMouseMove(new HoverEvent());
        };
        this.onMouseLeave = () => {
            const { onMouseLeave } = this.props;
            isFunction(onMouseLeave) && onMouseLeave(new HoverEvent());
        };
        this.onMouseEnter = () => {
            const { onMouseEnter } = this.props;
            isFunction(onMouseEnter) && onMouseEnter(new HoverEvent());
        };
    }
    getClassName() {
        const { bold, className, enabled, fontFamily, size } = this.props;
        return classNames({
            'c-link': true,
            [`c-link--bold`]: Boolean(bold),
            'c-link--disabled': !enabled,
            [`c-link--${size}`]: isEnumValid(Size, size),
            [`c-link--${fontFamily}`]: isEnumValid(LinkFontFamily, fontFamily),
            [`${className}`]: Boolean(className),
        });
    }
    renderIcon() {
        const { icon } = this.props;
        if (!icon) {
            return null;
        }
        if (isValidElement(icon)) {
            return icon;
        }
        return React.createElement(InlineImage, { className: "c-link__icon", src: icon });
    }
    renderText() {
        const { value } = this.props;
        if (!isContentfulString(value)) {
            return null;
        }
        return React.createElement("span", { className: "c-link__text", "x-contrast": "cl" }, MessageService.translate(value));
    }
    getBadgeValue() {
        const { badge } = this.props;
        if (!badge) {
            return null;
        }
        const value = parseInt(badge);
        if (!isNaN(value) && value < 1) {
            return null;
        }
        return badge;
    }
    render() {
        const { innerRef, target } = this.props;
        return (React.createElement(RouteContext.Context.Consumer, null, routeContext => (React.createElement("a", { className: this.getClassName(), "data-badge": this.getBadgeValue(), onClick: (event) => this.onClick(event, routeContext), onContextMenu: () => this.onContextMenuClick(), onDoubleClick: () => this.onDoubleClick(), onMouseEnter: this.onMouseEnter, onMouseLeave: this.onMouseLeave, onMouseOver: this.onMouseMove, ref: innerRef, rel: "noopener noreferrer", target: target },
            this.renderIcon(),
            this.renderText()))));
    }
};
Link.contextType = DataContext.Context;
Link.propTypes = {
    badge: string,
    bold: bool,
    className: string,
    enabled: bool,
    fontFamily: oneOf(enumValues(LinkFontFamily)),
    icon: oneOfType([string, element]),
    innerRef: func.notEnumerable(),
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onMouseEnter: eventListener,
    onMouseLeave: eventListener,
    onMouseMove: eventListener,
    preventDefault: bool.group('events'),
    route: string,
    size: oneOf(enumValues(Size)),
    stopPropagation: bool.group('events'),
    target: oneOf(enumValues(LinkTarget)),
    value: expression,
};
Link.defaultProps = {
    enabled: true,
    fontFamily: LinkFontFamily.MEDIUM,
    size: Size.SMALL,
    target: LinkTarget.BLANK,
};
Link = __decorate([
    AwesomeComponent({ api: LinkAPI, type: ComponentType.NAVIGATION })
], Link);
const LinkWithRef = forwardRef((props, ref) => React.createElement(Link, { ...props, innerRef: ref }));
LinkWithRef.displayName = Link.name;
export { LinkWithRef as Link, };
//# sourceMappingURL=link.js.map